import _ from "lodash";
import Reflux from "reflux";

import SearchStore from "./SearchStore";
import { SessionStore } from "../uidispatcherapi/SessionStore";
import RestrictionsStore from "./RestrictionsStore";

import * as TrackingUtil from "../utils/TrackingUtil";
import { DEFAULT_ROWS_LIST } from "../utils/SearchUtil";
import StoreListSearchActions from "../actions/StoreListSearchActions";

import CustomerSettingsStore from "./settings/CustomerSettingsStore";
import { SearchResultState, SearchQuery, Pagination, ContextInfo } from "../datatypes/SearchResult";

const defaultPagination = (): Pagination => {
    return { page: 1, rows: DEFAULT_ROWS_LIST };
};

type State = {
    searchState: {
        searchQuery: SearchQuery | null | undefined;
        numPagesSearch: number | null | undefined;
        contextInfo: ContextInfo | null | undefined;
    };
    searchResult: SearchResultState | null | undefined;
    pagination: Pagination;
    storeListId: string | null | undefined;
    articleIds: Array<string>;
};

const getInitialState = (): State => {
    return {
        searchState: {
            searchQuery: null,
            numPagesSearch: null,
            contextInfo: null
        },
        searchResult: null,
        pagination: defaultPagination(),
        storeListId: null,
        articleIds: []
    };
};

let state: State = getInitialState();

const StoreListResultStore = Reflux.createStore({
    init() {
        this.listenTo(StoreListSearchActions.query, this.doSearch);
        this.listenTo(SearchStore, this.handleSearchUpdate);
        this.listenTo(CustomerSettingsStore, this.doSearchAgain);
    },

    doSearchAgain() {
        if (state.searchState.searchQuery) {
            this.doSearch(
                state.searchState.searchQuery,
                state.searchState.numPagesSearch,
                state.articleIds,
                state.storeListId
            );
        }
    },

    doSearch(data: SearchQuery, numPagesSearch: number, articleIds: Array<string>, storeListId: string) {
        state.storeListId = storeListId;
        state.articleIds = articleIds;
        RestrictionsStore.doSearchWithRestriction(
            articleIds,
            restrictionId => {
                data.restrictionId = restrictionId;
                this.doSearchWithRestriction(data, numPagesSearch);
            },
            error => {
                TrackingUtil.trackUIError(
                    "StoreListResultStore",
                    "Fetching restricted article IDs in StoreListResultStore failed.",
                    error
                );
            }
        );
    },

    doSearchWithRestriction(data: SearchQuery, numPagesSearch: number) {
        const store = SessionStore.getCustomerStore();
        if (!store || !_.get(data, "restrictionId")) {
            return;
        }
        const contextInfo = {
            selectedStoreId: store,
            locale: SessionStore.getLocale(),
            country: SessionStore.getCountry(),
            customerId: SessionStore.getCustomerId(),
            loggedIn: SessionStore.loggedIn(),
            fsdFilterActive: CustomerSettingsStore.isFsdFilterActive()
        };

        const options = contextInfo.fsdFilterActive ? [{ key: "fsd", value: true }] : [];
        data.options = (data.options || []).concat(options);

        const currentState = state.searchState;
        if (
            _.isEqual(contextInfo, currentState.contextInfo) &&
            _.isEqual(currentState.searchQuery, data) &&
            numPagesSearch === currentState.numPagesSearch
        ) {
            return;
        }

        const isNewSearch =
            !_.isEqual(currentState.searchQuery, data) ||
            !currentState.numPagesSearch ||
            !(numPagesSearch > 1) ||
            numPagesSearch === currentState.numPagesSearch;

        if (isNewSearch) {
            state.searchResult = null;
            state.pagination = { page: 1, rows: numPagesSearch * DEFAULT_ROWS_LIST };
        } else {
            state.pagination = { page: numPagesSearch, rows: DEFAULT_ROWS_LIST };
        }
        state.searchState = {
            searchQuery: data,
            numPagesSearch: numPagesSearch,
            contextInfo: contextInfo
        };

        const page = isNewSearch ? 1 : numPagesSearch;
        const rows = isNewSearch ? numPagesSearch * DEFAULT_ROWS_LIST : DEFAULT_ROWS_LIST;
        const paramsForSearch = Object.assign({}, data, contextInfo || {}, {
            page,
            rows,
            more: !isNewSearch,
            onlySearchable: true
        });
        SearchStore.doSearch(paramsForSearch);
    },

    handleSearchUpdate(data: SearchResultState) {
        if (SearchStore.storeListPurpose === data.searchPurpose) {
            state.searchResult = _.cloneDeep(data);
            this.trigger();
        }
    },

    getState(): SearchResultState | null | undefined {
        return state.searchResult;
    },

    getStoreListId(): string | null | undefined {
        return state.storeListId;
    },

    /* For testing only */
    resetState() {
        state = getInitialState();
    }
});

export default StoreListResultStore;
